import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Users, FileText, Phone, Briefcase, Gift, Calendar, MessageSquare, Star, Filter, X, CalendarIcon, Download, ChevronDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import { ToastContainer, toast } from 'react-toastify';

// Import your API functions
import { getDashboard, getDashboardBrochure, getDashboardCallback, getDashboardContactFormSubmissions, getDashboardWalkin, } from 'Services/allApi';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Download states
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    // Only fetch for non-custom periods automatically
    if (selectedPeriod !== 'custom') {
      fetchDashboardData();
    }
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      let queryParams = `?period=${selectedPeriod}`;

      if (selectedPeriod === 'custom' && startDate && endDate) {
        queryParams += `&start_date=${startDate}&end_date=${endDate}`;
      }

      const response = await getDashboard(queryParams);
      console.log("Dashboard Response:", response);

      if (response.error === true || (!response.data && response.error)) {
        setError(response.message || 'Failed to load dashboard data');
      } else if (response.data) {
        setDashboardData(response.data);
        setError(null);
      } else {
        setError('No data received from server');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setStartDate('');
      setEndDate('');
    }
  };

  const handleApplyCustomDate = () => {
    if (startDate && endDate) {
      setShowCustomDatePicker(false);
      fetchDashboardData();
    }
  };

  const handleResetFilters = () => {
    setSelectedPeriod('today');
    setStartDate('');
    setEndDate('');
    setShowCustomDatePicker(false);
  };

  const handleDownloadData = async (type, label) => {
    setDownloading(true);
    setShowDownloadMenu(false);

    try {
      if (!startDate || !endDate) {
        toast.info("Please select both start and end dates before downloading.");
        setDownloading(false);
        return;
      }

      let response;
      let sheetName = label;

      // ✅ Use only start_date and end_date as query params (no period)
      const queryParams = `?start_date=${startDate}&end_date=${endDate}`;

      // ✅ Choose correct API based on type
      switch (type) {
        case 'brochure':
          response = await getDashboardBrochure(queryParams);
          break;
        case 'contact':
          response = await getDashboardContactFormSubmissions(queryParams);
          break;
        case 'walkin':
          response = await getDashboardWalkin(queryParams);
          break;
        case 'callback':
          response = await getDashboardCallback(queryParams);
          break;
        default:
          throw new Error('Invalid download type');
      }

      if (response.error || !response.data || response.data.length === 0) {
        toast.error('No data available for this range.');
        setDownloading(false);
        return;
      }

      // ✅ Prepare Excel sheet
      const worksheet = XLSX.utils.json_to_sheet(response.data.results.results);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // ✅ File name includes the label and date range
      const fullFileName = `${label.replace(/\s+/g, '_')}_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(workbook, fullFileName);

    } catch (error) {
      // console.error('Download error:', error);
      toast.error('Failed to download data. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const getIcon = (formType) => {
    const icons = {
      'Contact Form Submissions': <MessageSquare className="h-5 w-5" />,
      'Brochure Requests': <FileText className="h-5 w-5" />,
      'Callback Requests': <Phone className="h-5 w-5" />,
      'Trainer Applications': <Users className="h-5 w-5" />,
      'Hire Requests': <Briefcase className="h-5 w-5" />,
      'Job Applications': <Briefcase className="h-5 w-5" />,
      'Giveaway Entries': <Gift className="h-5 w-5" />,
      'Walk-in Registrations': <Calendar className="h-5 w-5" />,
      'Career Guidance Requests': <Star className="h-5 w-5" />,
      'Feedback Submissions': <MessageSquare className="h-5 w-5" />
    };
    return icons[formType] || <FileText className="h-5 w-5" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    });
  };

  const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#84cc16'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-600 text-lg font-semibold mb-2">Error Loading Dashboard</p>
          <p className="text-red-500">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600">No data available</p>
      </div>
    );
  }

  const { overview, form_breakdown, growth_comparison, feedback_stats } = dashboardData;

  // Prepare chart data
  const formChartData = form_breakdown
    .filter(item => item.count > 0)
    .map(item => ({
      name: item.form_type.replace(' Submissions', '').replace(' Requests', ''),
      value: item.count
    }));

  const growthData = Object.entries(growth_comparison)
    .filter(([key]) => key !== 'total_submissions')
    .map(([key, value]) => ({
      name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      current: value.current,
      previous: value.previous,
      growth: value.growth_percentage
    }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Filters and Download */}
        <div className="mb-8">
          {/* Filter Bar */}
          <div className="bg-white rounded-xl shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-purple-600" />
                <span className="font-semibold text-gray-700">Filter by Period</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Download Button with Dropdown */}
                {/* ✅ Download Button - Only Show When Custom Period */}
                {selectedPeriod === 'custom' && (
                  <div className="relative">
                    <button
                      onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                      disabled={downloading}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="h-4 w-4" />
                      {downloading ? 'Downloading...' : 'Download Data'}
                      <ChevronDown className="h-4 w-4" />
                    </button>

                    {/* ✅ Dropdown Menu */}
                    {showDownloadMenu && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                        <div className="p-2">
                          <button
                            onClick={() => handleDownloadData('brochure', 'Brochure Requests')}
                            className="w-full text-left px-4 py-3 hover:bg-purple-50 rounded-lg transition flex items-center gap-3"
                          >
                            <FileText className="h-5 w-5 text-purple-600" />
                            <span className="font-medium">Brochure Requests</span>
                          </button>

                          <button
                            onClick={() => handleDownloadData('contact', 'Contact Form Submissions')}
                            className="w-full text-left px-4 py-3 hover:bg-cyan-50 rounded-lg transition flex items-center gap-3"
                          >
                            <MessageSquare className="h-5 w-5 text-cyan-600" />
                            <span className="font-medium">Contact Form Submissions</span>
                          </button>

                          <button
                            onClick={() => handleDownloadData('walkin', 'Walk-in Registrations')}
                            className="w-full text-left px-4 py-3 hover:bg-green-50 rounded-lg transition flex items-center gap-3"
                          >
                            <Calendar className="h-5 w-5 text-green-600" />
                            <span className="font-medium">Walk-in Registrations</span>
                          </button>

                          <button
                            onClick={() => handleDownloadData('callback', 'Callback Requests')}
                            className="w-full text-left px-4 py-3 hover:bg-amber-50 rounded-lg transition flex items-center gap-3"
                          >
                            <Phone className="h-5 w-5 text-amber-600" />
                            <span className="font-medium">Callback Requests</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={handleResetFilters}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="h-4 w-4" />
                  Reset
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {['today', 'yesterday', 'week', 'month', 'year', 'custom'].map((period) => (
                <button
                  key={period}
                  onClick={() => handlePeriodChange(period)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${selectedPeriod === period
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>

            {/* Custom Date Picker */}
{showCustomDatePicker && (
  <div className="bg-gray-50 rounded-lg p-4 border-2 border-purple-200">
    <div className="flex items-center gap-2 mb-3">
      <CalendarIcon className="h-5 w-5 text-purple-600" />
      <span className="font-semibold text-gray-700">Custom Date Range</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Start Date
        </label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          End Date
        </label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>
      <div className="flex items-end">
        <button
          onClick={handleApplyCustomDate}
          disabled={!startDate || !endDate}
          className={`w-full px-4 py-2 rounded-lg font-medium transition ${startDate && endDate
              ? 'bg-purple-600 text-white hover:bg-purple-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          Apply Filter
        </button>
      </div>
    </div>
  </div>
)}
          </div>

          {/* Period Info */}
          <p className="text-gray-600">
            Period: <span className="font-semibold capitalize">{overview.period}</span>
            <span className="mx-2">•</span>
            {formatDate(overview.start_date)} - {formatDate(overview.end_date)}
          </p>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                <FileText className="h-8 w-8" />
              </div>
              {growth_comparison.total_submissions.growth_percentage > 0 ? (
                <TrendingUp className="h-6 w-6" />
              ) : (
                <TrendingDown className="h-6 w-6" />
              )}
            </div>
            <h3 className="text-sm font-medium mb-1 opacity-90">Total Submissions</h3>
            <p className="text-3xl font-bold mb-1">{overview.total_submissions}</p>
            <p className="text-sm opacity-90">
              {growth_comparison.total_submissions.growth_percentage > 0 ? '+' : ''}
              {growth_comparison.total_submissions.growth_percentage}% from previous period
            </p>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                <MessageSquare className="h-8 w-8" />
              </div>
            </div>
            <h3 className="text-sm font-medium mb-1 opacity-90">Contact Submissions</h3>
            <p className="text-3xl font-bold mb-1">{overview.contact_submissions}</p>
            <p className="text-sm opacity-90">
              {growth_comparison.contact_submissions.change > 0 ? '+' : ''}
              {growth_comparison.contact_submissions.change} from previous
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                <Phone className="h-8 w-8" />
              </div>
            </div>
            <h3 className="text-sm font-medium mb-1 opacity-90">Callback Requests</h3>
            <p className="text-3xl font-bold mb-1">{overview.callback_requests}</p>
            <p className="text-sm opacity-90">
              {growth_comparison.callback_requests.change > 0 ? '+' : ''}
              {growth_comparison.callback_requests.change} from previous
            </p>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                <FileText className="h-8 w-8" />
              </div>
            </div>
            <h3 className="text-sm font-medium mb-1 opacity-90">Brochure Requests</h3>
            <p className="text-3xl font-bold mb-1">{overview.brochure_requests}</p>
            <p className="text-sm opacity-90">
              {growth_comparison.brochure_requests.change > 0 ? '+' : ''}
              {growth_comparison.brochure_requests.change} from previous
            </p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Form Breakdown Pie Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Submission Distribution</h2>
            {formChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={formChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                     minAngle={3}
                     paddingAngle={1}
                    dataKey="value"
                  >
                    {formChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />

                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>No data available for this period</p>
              </div>
            )}
          </div>

          {/* Growth Comparison Bar Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Current vs Previous Period</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="current" fill="#8b5cf6" name="Current" />
                <Bar dataKey="previous" fill="#d8b4fe" name="Previous" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed Form Breakdown */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Form Submissions Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {form_breakdown.map((form, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition hover:border-purple-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-purple-100 rounded-lg p-2 text-purple-600">
                    {getIcon(form.form_type)}
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${form.count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                    {form.percentage.toFixed(0)}%
                  </span>
                </div>
                <h3 className="font-semibold text-gray-800 mb-1 text-sm">{form.form_type}</h3>
                <p className="text-2xl font-bold text-purple-600 mb-2">{form.count}</p>
                <p className="text-xs text-gray-500">
                  Latest: {formatDate(form.latest_submission)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Feedback Stats */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Feedback Overview</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Feedback</span>
                <span className="font-bold text-xl text-purple-600">{feedback_stats.total}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending</span>
                <span className="font-bold text-xl text-amber-600">{feedback_stats.pending_count}</span>
              </div>
            </div>
          </div>

          {/* Applications */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Applications</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Trainer Apps</span>
                <span className="font-bold text-xl text-cyan-600">{overview.trainer_applications}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Job Apps</span>
                <span className="font-bold text-xl text-emerald-600">{overview.job_applications}</span>
              </div>
            </div>
          </div>

          {/* Other Submissions */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Other Submissions</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Walk-ins</span>
                <span className="font-bold text-xl text-indigo-600">{overview.walkin_registrations}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Giveaways</span>
                <span className="font-bold text-xl text-pink-600">{overview.giveaway_entries}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop closeButton={true} />

      {/* Click outside to close dropdown */}
      {showDownloadMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDownloadMenu(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;